import { create } from "zustand";
import { io } from "socket.io-client";
import type { Socket } from "socket.io-client";
import type {
  Ack,
  BotLevel,
  ChatMessage,
  ClientMessageName,
  ClientMessagePayload,
  CreateRoomResponse,
  GameEvent,
  GameOptions,
  PlayerAction,
  RoomInfoResponse,
  RoomView,
  ServerToClientEvents,
} from "@grundbesitz/shared";

/**
 * Central client state. The server is the only authority: the client stores what the server sent
 * and sends intents back. Components read with `useStore(selector)` and call the actions below.
 */

export type ConnectionState = "idle" | "connecting" | "connected" | "disconnected";

export interface Toast {
  id: number;
  kind: "info" | "success" | "error";
  text: string;
}

export interface EventBatch {
  id: number;
  version: number;
  events: GameEvent[];
}

export interface Reaction {
  id: number;
  playerId: string;
  emoji: string;
}

interface StoreState {
  connection: ConnectionState;
  /** Room code of the current session, null on the start page. */
  code: string | null;
  room: RoomView | null;
  /** Reason the last join failed (unknown room, full ...). */
  joinError: { code: string; message: string } | null;
  /** serverNow - Date.now() in ms. Use `serverNow()` instead of reading this. */
  clockOffset: number;
  /** Last 200 event batches, newest last. Log panels derive their entries from this. */
  eventBatches: EventBatch[];
  toasts: Toast[];
  reactions: Reaction[];
  soundOn: boolean;
  /** Read cards and announcements aloud. Only plays while `soundOn` is set too. */
  voiceOn: boolean;
  theme: "light" | "dark";
}

interface StoreActions {
  /** Joins (or re-joins after a reload) a room. The server recognizes returning players by cookie. */
  join(code: string, name: string): Promise<Ack>;
  leave(): void;
  /** Sends a game intent. Adds the client's known version so stale actions are rejected. */
  act(action: PlayerAction, opts?: { skipVersion?: boolean }): Promise<Ack>;
  lobbyUpdate(patch: ClientMessagePayload<"lobby:update">): Promise<Ack>;
  setOptions(options: GameOptions): Promise<Ack>;
  kick(playerId: string): Promise<Ack>;
  addBot(level?: BotLevel): Promise<Ack>;
  setBotLevel(playerId: string, level: BotLevel): Promise<Ack>;
  removeBot(playerId: string): Promise<Ack>;
  startGame(): Promise<Ack>;
  sendChat(text: string): Promise<Ack>;
  react(emoji: ClientMessagePayload<"chat:reaction">["emoji"]): Promise<Ack>;
  toast(text: string, kind?: Toast["kind"]): void;
  dismissToast(id: number): void;
  setSound(on: boolean): void;
  setVoice(on: boolean): void;
  setTheme(theme: "light" | "dark"): void;
  /** Subscribes to game events as they arrive (animations, sounds). Returns the unsubscribe function. */
  subscribeEvents(cb: (batch: EventBatch) => void): () => void;
}

export type Store = StoreState & StoreActions;

let socket: Socket<ServerToClientEvents> | null = null;
let batchId = 0;
let toastId = 0;
let reactionId = 0;
const listeners = new Set<(batch: EventBatch) => void>();

const safeGet = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};
const safeSet = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* storage can be blocked, the page works without it */
  }
};

export const savedName = () => safeGet("gb.name") ?? "";
export const saveName = (name: string) => safeSet("gb.name", name);

function initialTheme(): "light" | "dark" {
  const saved = safeGet("gb.theme");
  if (saved === "light" || saved === "dark") return saved;
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function emitToServer<K extends ClientMessageName>(event: K, payload: ClientMessagePayload<K>): Promise<Ack> {
  return new Promise((resolve) => {
    if (!socket || !socket.connected) {
      resolve({ ok: false, code: "OFFLINE", message: "Keine Verbindung zum Server." });
      return;
    }
    // Timeout so a lost ack never leaves a button stuck.
    const timer = setTimeout(() => resolve({ ok: false, code: "TIMEOUT", message: "Der Server antwortet nicht." }), 8000);
    (socket as unknown as Socket).emit(event, payload, (ack: Ack) => {
      clearTimeout(timer);
      resolve(ack);
    });
  });
}

/** Feeds a room view and events into the store as if they came from the server (demo mode, tests). */
export function injectRoom(room: RoomView, events: GameEvent[] = []): void {
  useStore.setState({ room, clockOffset: room.timers.serverNow - Date.now() });
  if (events.length > 0) {
    const batch: EventBatch = { id: ++batchId, version: room.game?.version ?? 0, events };
    useStore.setState((st) => ({ eventBatches: [...st.eventBatches, batch].slice(-200) }));
    listeners.forEach((cb) => cb(batch));
  }
}

export const useStore = create<Store>((set, get) => {
  const surface = (ack: Ack): Ack => {
    if (!ack.ok) get().toast(ack.message, "error");
    return ack;
  };

  function ensureSocket() {
    if (socket) return socket;
    const s: Socket<ServerToClientEvents> = io({ withCredentials: true, transports: ["websocket", "polling"] });
    socket = s;
    set({ connection: "connecting" });
    s.on("connect", () => {
      set({ connection: "connected" });
      const { code } = get();
      if (code) void get().join(code, savedName() || "Gast");
    });
    s.on("disconnect", () => set({ connection: "disconnected" }));
    s.on("connect_error", () => set({ connection: "disconnected" }));
    s.on("room:state", (view) => {
      set({ room: view, clockOffset: view.timers.serverNow - Date.now() });
    });
    s.on("game:events", ({ version, events }) => {
      const batch: EventBatch = { id: ++batchId, version, events };
      set((st) => ({ eventBatches: [...st.eventBatches, batch].slice(-200) }));
      listeners.forEach((cb) => cb(batch));
    });
    s.on("chat:message", (msg: ChatMessage) => {
      set((st) => (st.room ? { room: { ...st.room, chat: [...st.room.chat, msg].slice(-100) } } : st));
    });
    s.on("chat:reaction", ({ playerId, emoji }) => {
      const r: Reaction = { id: ++reactionId, playerId, emoji };
      set((st) => ({ reactions: [...st.reactions, r] }));
      setTimeout(() => set((st) => ({ reactions: st.reactions.filter((x) => x.id !== r.id) })), 3500);
    });
    s.on("room:kicked", ({ reason }) => {
      get().toast(reason, "error");
      get().leave();
    });
    s.on("error", ({ message }) => get().toast(message, "error"));
    return s;
  }

  return {
    connection: "idle",
    code: null,
    room: null,
    joinError: null,
    clockOffset: 0,
    eventBatches: [],
    toasts: [],
    reactions: [],
    soundOn: safeGet("gb.sound") !== "off",
    voiceOn: safeGet("gb.voice") !== "off",
    theme: initialTheme(),

    async join(code, name) {
      ensureSocket();
      set({ code, joinError: null });
      if (!socket!.connected) return { ok: false, code: "OFFLINE", message: "Verbinde ..." };
      const ack = await emitToServer("room:join", { code, name });
      if (!ack.ok) {
        set({ joinError: { code: ack.code, message: ack.message }, room: null });
      } else {
        saveName(name);
      }
      return ack;
    },

    leave() {
      if (socket?.connected) (socket as unknown as Socket).emit("room:leave", {}, () => undefined);
      set({ code: null, room: null, joinError: null, eventBatches: [] });
    },

    async act(action, opts) {
      const version = get().room?.game?.version;
      const withVersion =
        !opts?.skipVersion && version !== undefined ? ({ ...action, expectedVersion: version } as PlayerAction) : action;
      return surface(await emitToServer("game:action", { action: withVersion }));
    },
    async lobbyUpdate(patch) {
      return surface(await emitToServer("lobby:update", patch));
    },
    async setOptions(options) {
      return surface(await emitToServer("lobby:options", options));
    },
    async kick(playerId) {
      return surface(await emitToServer("lobby:kick", { playerId }));
    },
    async addBot(level) {
      return surface(await emitToServer("lobby:addBot", level ? { level } : {}));
    },
    async setBotLevel(playerId, level) {
      return surface(await emitToServer("lobby:botLevel", { playerId, level }));
    },
    async removeBot(playerId) {
      return surface(await emitToServer("lobby:removeBot", { playerId }));
    },
    async startGame() {
      return surface(await emitToServer("lobby:start", {}));
    },
    async sendChat(text) {
      return surface(await emitToServer("chat:send", { text }));
    },
    async react(emoji) {
      return emitToServer("chat:reaction", { emoji });
    },

    toast(text, kind = "info") {
      const t: Toast = { id: ++toastId, kind, text };
      set((st) => ({ toasts: [...st.toasts, t].slice(-5) }));
      setTimeout(() => get().dismissToast(t.id), kind === "error" ? 6000 : 4000);
    },
    dismissToast(id) {
      set((st) => ({ toasts: st.toasts.filter((t) => t.id !== id) }));
    },
    setSound(on) {
      safeSet("gb.sound", on ? "on" : "off");
      set({ soundOn: on });
    },
    setVoice(on) {
      safeSet("gb.voice", on ? "on" : "off");
      set({ voiceOn: on });
    },
    setTheme(theme) {
      safeSet("gb.theme", theme);
      document.documentElement.dataset.theme = theme;
      set({ theme });
    },
    subscribeEvents(cb) {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
  };
});

/** Server time in epoch ms, corrected for clock skew. */
export function serverNow(): number {
  return Date.now() + useStore.getState().clockOffset;
}

/** Whole seconds left until `at` (a server timestamp), never negative. */
export function secondsUntil(at: number | null | undefined): number | null {
  if (!at) return null;
  return Math.max(0, Math.ceil((at - serverNow()) / 1000));
}

// ---- HTTP helpers (no socket needed)

export async function createRoom(name: string): Promise<CreateRoomResponse> {
  const res = await fetch("/api/rooms", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error("Das Spiel konnte nicht erstellt werden.");
  return res.json();
}

export async function roomInfo(code: string): Promise<RoomInfoResponse> {
  const res = await fetch(`/api/rooms/${encodeURIComponent(code)}`, { credentials: "include" });
  if (!res.ok) return { exists: false };
  return res.json();
}
